<template>
  <div class="bkash-campaign-page">
    <a-row :gutter="[16, 16]">
      <a-col :span="24">
        <a-card :bordered="false" class="filter-card" title="Bkash Campaign">
          <div class="filter-toolbar">
            <div class="filter-field">
              <div class="filter-label">Campaign</div>
              <a-select
                v-model="selectedCampaign"
                show-search
                placeholder="Select campaign"
                :loading="campaignsLoading"
                :not-found-content="campaignsLoading ? undefined : 'No campaigns'"
                allow-clear
                class="w-100 filter-control"
              >
                <a-select-option
                  v-for="opt in campaignOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </a-select-option>
              </a-select>
            </div>
            <div class="filter-field filter-field--range">
              <div class="filter-label">Date range</div>
              <a-range-picker
                v-model="dateRange"
                class="w-100 filter-control"
                format="YYYY-MM-DD"
                :placeholder="['Start date', 'End date']"
                @change="onDateRangeChange"
              />
            </div>
            <div class="filter-field filter-field--submit">
              <div class="filter-label filter-label--spacer" aria-hidden="true">&nbsp;</div>
              <a-button
                type="primary"
                class="filter-control filter-submit-btn"
                block
                :loading="dataLoading"
                :disabled="!canSubmit"
                @click="fetchCampaignData"
              >
                Submit
              </a-button>
            </div>
          </div>
          <p v-if="distinctError" class="error-text">{{ distinctError }}</p>
        </a-card>
      </a-col>

      <a-col :span="24">
        <a-card :bordered="false" class="results-card" title="Campaign data">
          <a-alert
            v-if="dataError && dataFetchAttempted"
            type="error"
            show-icon
            :message="dataError"
            class="results-alert"
          />
          <a-spin :spinning="dataLoading">
            <template v-if="dataFetchAttempted && !dataLoading">
              <div v-if="!dataError && !tableRows.length" class="empty-wrap">
                <a-empty description="No Data Found" />
              </div>
              <a-table
                v-else-if="tableRows.length"
                :columns="tableColumns"
                :data-source="tableRows"
                :row-key="getRowKey"
                :pagination="tablePagination"
                :scroll="{ x: 'max-content' }"
                size="middle"
                bordered
              />
            </template>
            <div v-else-if="!dataLoading" class="hint-text">
              Select a campaign, choose a date range, and press Submit to load data.
            </div>
          </a-spin>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import axios from "axios";

const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;

function unwrapArray(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== "object") return [];

  const candidates = [
    payload.data,
    payload.results,
    payload.items,
    payload.records,
    payload.rows,
    payload.campaigns,
    payload.distinct_campaigns,
    payload.distinctCampaigns,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (Array.isArray(c)) return c;
  }
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    const inner = payload.data.data || payload.data.items || payload.data.records;
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

function normalizeCampaignOptions(rawList) {
  return rawList
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        const s = String(item);
        if (!s.trim()) return null;
        return { label: s, value: s };
      }
      if (item && typeof item === "object") {
        const value =
          item.campaign_id ??
          item.campaign ??
          item.slug ??
          item.name ??
          item.value ??
          item.id ??
          item.code;
        if (value == null || String(value).trim() === "") {
          return null;
        }
        const v = String(value);
        const label =
          item.label ??
          item.name ??
          item.campaign ??
          item.title ??
          (item.campaign_id != null && item.campaign_id !== ""
            ? String(item.campaign_id)
            : v);
        return { label: String(label), value: v };
      }
      const s = String(item);
      return { label: s, value: s };
    })
    .filter(Boolean);
}

function rowsFromPayload(payload) {
  const direct = unwrapArray(payload);
  if (direct.length) return direct;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const single = payload.data;
    if (single && typeof single === "object" && !Array.isArray(single)) {
      return [single];
    }
  }
  return [];
}

function titleizeKey(key) {
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default {
  name: "BkashCampaign",
  data() {
    return {
      campaignOptions: [],
      campaignsLoading: false,
      distinctError: "",
      selectedCampaign: undefined,
      dateRange: [],
      startDate: "",
      endDate: "",
      dataLoading: false,
      dataFetchAttempted: false,
      tableColumns: [],
      tableRows: [],
      dataError: "",
    };
  },
  computed: {
    canSubmit() {
      return (
        !!this.selectedCampaign &&
        this.startDate.length > 0 &&
        this.endDate.length > 0
      );
    },
    tablePagination() {
      if (this.tableRows.length <= 10) return false;
      return { pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] };
    },
  },
  mounted() {
    this.loadDistinctCampaigns();
  },
  methods: {
    getRowKey(record) {
      return record.__rowKey;
    },
    onDateRangeChange(_, dateStrings) {
      this.startDate = dateStrings[0] || "";
      this.endDate = dateStrings[1] || "";
    },
    loadDistinctCampaigns() {
      this.distinctError = "";
      this.campaignsLoading = true;
      axios
        .get(`${ADMIN_URL}/distinct-campaign`)
        .then((res) => {
          const list = unwrapArray(res.data);
          const rawOpts = normalizeCampaignOptions(list);
          const seen = new Set();
          this.campaignOptions = rawOpts.filter((o) => {
            if (seen.has(o.value)) return false;
            seen.add(o.value);
            return true;
          });
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Failed to load campaigns.";
          this.distinctError = msg;
          this.$message.error(msg);
        })
        .finally(() => {
          this.campaignsLoading = false;
        });
    },
    fetchCampaignData() {
      if (!this.canSubmit) {
        this.$message.warning("Please select a campaign and a full date range.");
        return;
      }
      this.dataLoading = true;
      this.dataError = "";
      const params = {
        campaign: this.selectedCampaign,
        startDate: this.startDate,
        endDate: this.endDate,
      };
      axios
        .get(`${ADMIN_URL}/campaign-data`, { params })
        .then((res) => {
          this.dataError = "";
          const rows = rowsFromPayload(res.data);
          const normalized = rows.map((row, i) => {
            if (row && typeof row === "object" && !Array.isArray(row)) {
              return { ...row, __rowKey: `r-${i}` };
            }
            return { value: row, __rowKey: `r-${i}` };
          });
          this.tableRows = normalized;
          this.tableColumns = this.buildColumns(normalized);
          this.dataFetchAttempted = true;
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Failed to load campaign data.";
          this.dataError = msg;
          this.$message.error(msg);
          this.tableRows = [];
          this.tableColumns = [];
          this.dataFetchAttempted = true;
        })
        .finally(() => {
          this.dataLoading = false;
        });
    },
    buildColumns(rows) {
      if (!rows.length) return [];
      const first = rows[0];
      const keys = Object.keys(first).filter((k) => k !== "__rowKey");
      return keys.map((k) => ({
        title: titleizeKey(k),
        dataIndex: k,
        key: k,
        ellipsis: true,
      }));
    },
  },
};
</script>

<style scoped>
.bkash-campaign-page {
  padding: 0;
}

.filter-card,
.results-card {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
}

.filter-toolbar {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(220px, 1.35fr) minmax(112px, 140px);
  gap: 16px 20px;
  align-items: end;
}

@media (max-width: 991px) {
  .filter-toolbar {
    grid-template-columns: 1fr 1fr;
  }

  .filter-field--submit {
    grid-column: 1 / -1;
    max-width: 200px;
  }
}

@media (max-width: 575px) {
  .filter-toolbar {
    grid-template-columns: 1fr;
  }

  .filter-field--submit {
    grid-column: auto;
    max-width: none;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.filter-field--range {
  min-width: 0;
}

.filter-field--submit {
  min-width: 112px;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;
  line-height: 1.35;
  min-height: 1.35em;
}

.filter-label--spacer {
  visibility: hidden;
  user-select: none;
}

.filter-control {
  width: 100%;
}

.filter-submit-btn {
  height: 32px;
  padding: 0 15px;
  line-height: 30px;
}

.w-100 {
  width: 100%;
}

.error-text {
  color: #cf1322;
  margin-top: 12px;
  margin-bottom: 0;
}

.empty-wrap {
  padding: 32px 0;
}

.hint-text {
  color: rgba(0, 0, 0, 0.45);
  padding: 24px 0;
  text-align: center;
}

.results-alert {
  margin-bottom: 16px;
}
</style>
